import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ExternalLink, Link as LinkIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import MediaUrlsInput, { absolutize } from '../../components/MediaUrlsInput';
import TranslationsEditor from '../../components/TranslationsEditor';
import BulkTranslateButton from '../../components/BulkTranslateButton';
import DefaultsControls from '../../components/DefaultsControls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerPackagesManager = () => {
  const [packages, setPackages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    destination: '',
    price: '',
    duration: '',
    rating: '4.5',
    image: '',
    media: [],
    description: '',
    highlights: '',
    supplier: '',
    supplier_url: '',
    supplier_email: '',
    booking_type: 'iframe',
    tags: '',
    translations: {}
  });

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/partner-packages`);
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to fetch partner packages');
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '', destination: '', price: '', duration: '',
      rating: '4.5', image: '', media: [], description: '', highlights: '',
      supplier: '', supplier_url: '',
      supplier_email: '', booking_type: 'iframe', tags: '',
      translations: {}
    });
    setEditingPkg(null);
  };

  const openCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEdit = (pkg) => {
    const media = (pkg.media && pkg.media.length > 0)
      ? pkg.media
      : (pkg.image ? [pkg.image] : []);
    setFormData({
      title: pkg.title || '',
      destination: pkg.destination || '',
      price: pkg.price || '',
      duration: pkg.duration || '',
      rating: pkg.rating?.toString() || '4.5',
      image: pkg.image || (media[0] || ''),
      media,
      description: pkg.description || '',
      highlights: (pkg.highlights || []).join('\n'),
      supplier: pkg.supplier || '',
      supplier_url: pkg.supplier_url || '',
      supplier_email: pkg.supplier_email || '',
      booking_type: pkg.booking_type || 'iframe',
      tags: (pkg.tags || []).join(', '),
      translations: pkg.translations || {}
    });
    setEditingPkg(pkg);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (!formData.media || formData.media.length === 0) {
      toast.error('Please upload or add at least one image/video.');
      return;
    }

    const payload = {
      ...formData,
      // Keep `image` synced with the first media item (cover) for backwards compat.
      image: formData.media[0],
      rating: parseFloat(formData.rating),
      highlights: formData.highlights.split('\n').map(s => s.trim()).filter(Boolean),
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
    };

    try {
      if (editingPkg) {
        await axios.put(`${API}/partner-packages/${editingPkg.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Partner package updated');
      } else {
        await axios.post(`${API}/partner-packages`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Partner package created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error('Failed to save partner package');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partner package?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/partner-packages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner package deleted');
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="partner-packages-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-heading font-bold text-maroon">Partner Packages</h2>
          <p className="text-maroon/60 text-sm mt-1">Manage featured packages with supplier URLs for the Partners page</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DefaultsControls
            section="partner-packages"
            reseedConfirm={
              'Re-seed default partner packages from your last saved snapshot?\n\n' +
              'Replaces every package whose title is in the snapshot. If no snapshot has been saved yet, ' +
              'falls back to the original 3 packages (Amalfi Coast, Positano & Ravello, Patagonia Lodge).'
            }
            onComplete={fetchPackages}
            reseedTestId="reseed-partner-packages-btn"
            snapshotTestId="snapshot-partner-packages-btn"
          />
          <BulkTranslateButton scope="partner-packages" onComplete={fetchPackages} />
          <Button onClick={openCreate} className="btn-primary" data-testid="add-partner-pkg-btn">
            <Plus size={20} className="mr-2" />
            Add Package
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {packages.length === 0 ? (
          <div className="card text-center py-12">
            <LinkIcon size={40} className="mx-auto text-maroon/20 mb-3" />
            <p className="text-maroon/60">No partner packages yet. Add your first one above.</p>
          </div>
        ) : (
          packages.map((pkg) => (
            <div key={pkg.id} className="card flex items-center justify-between" data-testid="partner-pkg-item">
              <div className="flex items-center space-x-4 flex-1 min-w-0">
                {pkg.image && (
                  <img src={absolutize(pkg.image)} alt={pkg.title} className="w-20 h-20 object-cover rounded-sm border border-maroon/10 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <h3 className="font-heading font-bold text-lg text-maroon truncate">{pkg.title}</h3>
                  <p className="text-maroon/60 text-sm">{pkg.destination} &middot; {pkg.price} &middot; {pkg.duration}</p>
                  <p className="text-xs text-maroon/40 mt-1">Supplier: {pkg.supplier}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <LinkIcon size={12} className="text-terracotta flex-shrink-0" />
                    <a
                      href={pkg.supplier_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-terracotta hover:underline truncate max-w-[300px]"
                    >
                      {pkg.supplier_url}
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <Button
                  onClick={() => openEdit(pkg)}
                  variant="outline"
                  size="sm"
                  className="border-maroon/20 text-maroon hover:bg-maroon/5"
                  data-testid="edit-partner-pkg-btn"
                >
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(pkg.id)}
                  variant="destructive"
                  size="sm"
                  data-testid="delete-partner-pkg-btn"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">
              {editingPkg ? 'Edit Partner Package' : 'Add Partner Package'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-pkg-form">
            <div className="space-y-2">
              <Label htmlFor="title">Package Title *</Label>
              <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="input-field" placeholder="e.g., Caribbean All-Inclusive Resort" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="destination">Destination *</Label>
                <Input id="destination" value={formData.destination} onChange={(e) => setFormData({ ...formData, destination: e.target.value })} required className="input-field" placeholder="e.g., Cancun, Mexico" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier Name *</Label>
                <Input id="supplier" value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} required className="input-field" placeholder="e.g., TravelDeals Pro" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="booking_type">Booking Method *</Label>
              <select
                id="booking_type"
                value={formData.booking_type}
                onChange={(e) => setFormData({ ...formData, booking_type: e.target.value })}
                className="w-full px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
                data-testid="booking-type-select"
              >
                <option value="iframe">Iframe (white-label IBE) — opens inside /package-viewer</option>
                <option value="external_link">External Link — opens supplier site in new tab</option>
                <option value="quote_request">Request Quote — captures lead, emails supplier</option>
              </select>
            </div>

            <div className="space-y-2 bg-terracotta/5 border border-terracotta/20 rounded-sm p-4">
              <Label htmlFor="supplier_url" className="flex items-center gap-2">
                <ExternalLink size={16} className="text-terracotta" />
                {formData.booking_type === 'quote_request' ? 'Reference URL (optional)' : 'Supplier Target URL *'}
              </Label>
              <Input
                id="supplier_url"
                value={formData.supplier_url}
                onChange={(e) => setFormData({ ...formData, supplier_url: e.target.value })}
                required={formData.booking_type !== 'quote_request'}
                className="input-field"
                placeholder="https://supplier-site.com/package-page"
              />
              <p className="text-xs text-maroon/50">
                {formData.booking_type === 'iframe' && 'Loads inside the branded /package-viewer iframe.'}
                {formData.booking_type === 'external_link' && 'Opens this URL in a new tab. Make sure your IATA/affiliate ID is in it.'}
                {formData.booking_type === 'quote_request' && 'Optional — used as a fallback link in the quote-request email.'}
              </p>
            </div>

            {formData.booking_type === 'quote_request' && (
              <div className="space-y-2">
                <Label htmlFor="supplier_email">Supplier Email (for leads)</Label>
                <Input
                  id="supplier_email"
                  type="email"
                  value={formData.supplier_email}
                  onChange={(e) => setFormData({ ...formData, supplier_email: e.target.value })}
                  className="input-field"
                  placeholder="bookings@supplier.com"
                />
                <p className="text-xs text-maroon/50">
                  Quote requests for this package will be emailed here, with you cc'd. Leave blank to use the
                  global default in <strong>Site Settings</strong>.
                </p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price *</Label>
                <Input id="price" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required className="input-field" placeholder="$1,299" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration *</Label>
                <Input id="duration" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} required className="input-field" placeholder="7 nights" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rating">Rating</Label>
                <Input id="rating" type="number" step="0.1" min="1" max="5" value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: e.target.value })} className="input-field" />
              </div>
            </div>

            <div className="space-y-2">
              <MediaUrlsInput
                urls={formData.media}
                onChange={(media) => setFormData({ ...formData, media })}
                helperText="Upload your own photos/videos, or paste URLs (YouTube/Vimeo OK). The first item becomes the package cover."
                testIdPrefix="pp-media"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Detailed description</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
                placeholder="Long-form copy shown in the 'Details' modal. Plain text — line breaks are preserved."
                className="w-full px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none resize-none"
                data-testid="pp-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="highlights">Highlights (one per line)</Label>
              <textarea
                id="highlights"
                value={formData.highlights}
                onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                rows={4}
                placeholder={'Daily breakfast included\nPrivate beachfront\nGuided cultural tour'}
                className="w-full px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none resize-none"
                data-testid="pp-highlights"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma separated)</Label>
              <Input id="tags" value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} className="input-field" placeholder="Beach, All-Inclusive, Family" />
            </div>

            <TranslationsEditor
              translations={formData.translations}
              onChange={(translations) => setFormData({ ...formData, translations })}
              sourceContent={{
                title: formData.title,
                destination: formData.destination,
                duration: formData.duration,
                description: formData.description,
                highlights: formData.highlights.split('\n').map((s) => s.trim()).filter(Boolean),
                tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
              }}
              fields={[
                { key: 'title', label: 'Title' },
                { key: 'destination', label: 'Destination' },
                { key: 'duration', label: 'Duration' },
                { key: 'description', label: 'Detailed description', type: 'textarea' },
              ]}
              languages={['es']}
            />

            <Button type="submit" className="btn-primary w-full">
              {editingPkg ? 'Update Package' : 'Create Package'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerPackagesManager;
